import { LoadingWrapper } from "components/common/layouts";
import LoadingIndicator from "components/common/layouts/LoadingIndicator";
import FeedbackIcon from "components/common/ui/FeedbackIcon";
import { Button } from "components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import { FormType } from "enums/form";
import { ReactNode, RefObject, useEffect, useRef } from "react";

type FeedbackSheetProps = {
  open: boolean;
  formType: FormType;
  onOpenChange: (open: boolean) => void;
  detailTitle: string;
  isLoading: boolean;
  feedbackTypeLabel: string;
  listTitle: string;
  list: ReactNode;
  detail: ReactNode;
  detailSectionRef?: RefObject<HTMLDivElement>;
  shouldScrollToListSection?: boolean;
  showViewMore?: boolean;
  isFetchingNextPage?: boolean;
  onViewMore?: () => void;
};

const FeedbackSheet = ({
  open,
  formType,
  onOpenChange,
  detailTitle,
  isLoading,
  feedbackTypeLabel,
  listTitle,
  list,
  detail,
  detailSectionRef,
  shouldScrollToListSection = false,
  showViewMore = false,
  isFetchingNextPage = false,
  onViewMore,
}: FeedbackSheetProps) => {
  const listSectionElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !shouldScrollToListSection || isLoading) {
      return;
    }

    const target = listSectionElementRef.current;
    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [open, shouldScrollToListSection, isLoading, listTitle]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" showCloseButton={false}>
        <SheetHeader>
          <div className="flex items-center gap-2">
            <FeedbackIcon type={formType} />
            <SheetTitle className="body-20-semibold">
              {feedbackTypeLabel}
            </SheetTitle>
          </div>
          <SheetClose />
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div ref={detailSectionRef}>
            <LoadingWrapper isLoading={isLoading}>
              <h3 className="body-16-semibold mb-4">{detailTitle}</h3>
              {detail}
            </LoadingWrapper>
          </div>

          <div ref={listSectionElementRef} className="mt-8 pt-6">
            <h3 className="body-16-semibold mb-4">{listTitle}</h3>
            {list}
            {isFetchingNextPage && (
              <div className="mt-4">
                <LoadingIndicator />
              </div>
            )}
            {showViewMore && !isFetchingNextPage && (
              <Button
                variant="link"
                className="mt-4 h-auto p-0"
                onClick={onViewMore}
              >
                View more
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FeedbackSheet;
